// Trading from the chart: the Buy and Sell buttons, the order ticket, the
// lines of open positions (stop and target draggable on hand-opened ones),
// and price alerts (create, list, drag on the chart).
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell, BellRing, RotateCcw, Trash2, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState, type PointerEvent as RPointerEvent, type ReactNode } from "react";
import { toast } from "sonner";
import type { ChartView } from "@/components/chart/TradingChart";
import type { ChartSettings } from "@/components/chart/settings";
import { chartColors } from "@/components/chart/theme";
import { Badge, Button, Callout, Empty, Field, Input, NumberInput, Segmented, Select, cn } from "@/components/ui/core";
import { Dialog, Sheet } from "@/components/ui/overlay";
import { api, errorMessage } from "@/lib/api";
import { ago, pnl, price as fmtPrice } from "@/lib/format";
import { subscribe } from "@/lib/live";
import type { Alert as LiveAlert, Instrument, Mode, Position, Quote } from "@/lib/types";

export type Side = "BUY" | "SELL";

/** ``p`` on the instrument's tick grid, without float noise (95.05, never 95.05000000000001,
 * which the server would round up a whole tick). */
export const roundTick = (p: number, tick = 0.05) => Number((Math.round(p / tick) * tick).toFixed(4));
const refPrice = (q: Quote | null | undefined, side: Side) => (q ? (side === "BUY" ? q.ask || q.ltp : q.bid || q.ltp) : null);

function defaultStop(inst: Instrument, side: Side, ref: number, s: ChartSettings): number | null {
  const pct = inst.kind === "OPTION" ? s.instantOptionStopPct : s.instantStopPct;
  if (!pct || pct <= 0) return null;
  return roundTick(side === "BUY" ? ref * (1 - pct / 100) : ref * (1 + pct / 100), inst.tick_size);
}

interface OrderBody {
  mode: Mode;
  key: string;
  side: Side;
  lots: number;
  product: string;
  stop?: number | null;
  target?: number | null;
  trail_points?: number | null;
}

export function usePlaceOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: OrderBody & { label: string }) => {
      const { label: _label, ...body } = b;
      void _label;
      return api.post<{ position_id: string; qty: number; ref_price: number }>("/api/orders/manual", body);
    },
    onSuccess: (r, b) => {
      toast.success(`${b.side === "BUY" ? "Buying" : "Selling"} ${r.qty} ${b.label} at ~${fmtPrice(r.ref_price)}`, {
        description: b.mode === "LIVE" ? "LIVE order sent to Angel One" : "Paper order",
      });
      qc.invalidateQueries({ queryKey: ["positions"] });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
}

// ── Buy / qty / Sell, on the chart legend ───────────────────────────────────
export function TradeButtons({ inst, quote, mode, lots, onLots, instant, settings, onTicket, compact }: {
  inst: Instrument;
  quote: Quote | null;
  mode: Mode;
  lots: number;
  onLots: (n: number) => void;
  instant: boolean;
  settings: ChartSettings;
  onTicket: (side: Side) => void;
  compact?: boolean;
}) {
  const place = usePlaceOrder();
  const buy = refPrice(quote, "BUY");
  const sell = refPrice(quote, "SELL");
  const go = (side: Side) => {
    if (!instant) return onTicket(side);
    const ref = refPrice(quote, side);
    if (!ref) return toast.error("No price yet for this instrument");
    place.mutate({ mode, key: inst.key, side, lots, product: "INTRADAY", stop: defaultStop(inst, side, ref, settings), label: inst.label });
  };
  const unit = inst.kind === "EQUITY" ? "Shares" : `Lots of ${inst.lot_size}`;
  // a phone's chart buttons are finger-sized
  const btn = compact ? "h-7 px-2 text-[11.5px] pointer-coarse:h-9 pointer-coarse:px-3 pointer-coarse:text-[12.5px]" : "h-[30px] px-2.5 text-[12.5px] pointer-coarse:h-9";
  return (
    <div className="flex items-center gap-1">
      <button
        onClick={() => go("BUY")}
        disabled={place.isPending}
        className={cn("rounded-[5px] bg-[#089981] font-semibold tracking-wide whitespace-nowrap text-white shadow-sm hover:brightness-110 active:brightness-90 disabled:opacity-60", btn)}
      >
        {compact ? "BUY" : "BUY @"} {buy ? fmtPrice(buy) : "n/a"}
      </button>
      <LotsBox lots={lots} onLots={onLots} unit={unit} compact={compact} />
      <button
        onClick={() => go("SELL")}
        disabled={place.isPending}
        className={cn("rounded-[5px] bg-[#f23645] font-semibold tracking-wide whitespace-nowrap text-white shadow-sm hover:brightness-110 active:brightness-90 disabled:opacity-60", btn)}
      >
        {compact ? "SELL" : "SELL @"} {sell ? fmtPrice(sell) : "n/a"}
      </button>
      {mode === "LIVE" && <Badge tone="live">LIVE</Badge>}
      {instant && <span className="text-[11px] text-faint">instant</span>}
    </div>
  );
}

/** The quantity box between Buy and Sell. It keeps what is typed: clearing it to
 * type 5 must give 5 lots, not 15 (a box that snaps back to 1 as it empties makes
 * "1" + "5"), and with instant orders that number is sent in one click. */
function LotsBox({ lots, onLots, unit, compact }: { lots: number; onLots: (n: number) => void; unit: string; compact?: boolean }) {
  const [text, setText] = useState(String(lots));
  useEffect(() => setText((t) => (Number(t) === lots ? t : String(lots))), [lots]);
  return (
    <input
      value={text}
      inputMode="numeric"
      onChange={(e) => {
        const t = e.target.value.replace(/\D/g, "").slice(0, 4);
        setText(t);
        const n = Number(t);
        if (t && n >= 1) onLots(n);
      }}
      onBlur={() => setText(String(lots))}
      className={cn("rounded-[5px] border border-line bg-panel/90 text-center num outline-none focus:border-accent", compact ? "h-7 w-9 text-[11.5px] pointer-coarse:h-9 pointer-coarse:w-11 pointer-coarse:text-[16px]" : "h-[30px] w-10 text-[12.5px] pointer-coarse:h-9 pointer-coarse:text-[16px]")}
      title={unit}
      aria-label={unit}
    />
  );
}

// ── an index cannot be traded: its legend offers its future and options ─────
export function IndexTrade({ future, options, onOpen, onChain, compact }: {
  future: Instrument | null;
  options: boolean;
  onOpen: (key: string) => void;
  onChain?: () => void;
  compact?: boolean;
}) {
  if (!future && !(options && onChain)) return null;
  // a phone's chart buttons are finger-sized
  const btn = compact ? "h-7 px-2 text-[11.5px] pointer-coarse:h-9 pointer-coarse:px-3 pointer-coarse:text-[12.5px]" : "h-[30px] px-2.5 text-[12.5px] pointer-coarse:h-9";
  const month = future?.expiry ? new Date(future.expiry).toLocaleDateString("en-IN", { month: "short" }) : "";
  return (
    <div className="flex items-center gap-1">
      {future && (
        <button
          onClick={() => onOpen(future.key)}
          title={`Open ${future.label} to buy or sell it`}
          className={cn("rounded-[5px] bg-[#2962ff] font-semibold whitespace-nowrap text-white shadow-sm hover:brightness-110 active:brightness-90", btn)}
        >
          {compact ? `Trade ${month} future` : `Trade the ${month} future`}
        </button>
      )}
      {options && onChain && (
        <button
          onClick={onChain}
          title="Pick an option to buy or sell"
          className={cn("rounded-[5px] border border-chart-line bg-chart/80 font-semibold whitespace-nowrap text-fg hover:bg-chart-hover", btn)}
        >
          {compact ? "Options" : "Option chain"}
        </button>
      )}
    </div>
  );
}

// ── the order ticket ────────────────────────────────────────────────────────
export function OrderTicket({ open, onOpenChange, inst, quote, mode, side: side0, lots: lots0, settings }: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  inst: Instrument;
  quote: Quote | null;
  mode: Mode;
  side: Side;
  lots: number;
  settings: ChartSettings;
}) {
  const [side, setSide] = useState<Side>(side0);
  const [lots, setLots] = useState(lots0);
  const [product, setProduct] = useState("INTRADAY");
  const [stop, setStop] = useState("");
  const [target, setTarget] = useState("");
  const [trail, setTrail] = useState("");
  const place = usePlaceOrder();
  const ref = refPrice(quote, side);
  useEffect(() => {
    if (!open) return;
    setSide(side0);
    setLots(lots0);
    const r = refPrice(quote, side0);
    const st = r ? defaultStop(inst, side0, r, { ...settings, instantOptionStopPct: settings.instantOptionStopPct || 25 }) : null;
    setStop(st ? st.toFixed(2) : "");
    setTarget(r && st ? roundTick(r + (r - st) * 2, inst.tick_size).toFixed(2) : "");
    setTrail("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const qty = lots * (inst.kind === "EQUITY" ? 1 : Math.max(1, inst.lot_size));
  const s = Number(stop) || null;
  const t = Number(target) || null;
  const risk = ref && s ? Math.abs(ref - s) * qty : null;
  const reward = ref && t ? Math.abs(t - ref) * qty : null;
  const wrongStop = ref && s ? (side === "BUY" ? s >= ref : s <= ref) : false;
  const wrongTarget = ref && t ? (side === "BUY" ? t <= ref : t >= ref) : false;
  const stopNeeded = !(side === "BUY" && inst.kind === "OPTION");
  const live = mode === "LIVE";
  const submit = () =>
    place.mutate(
      { mode, key: inst.key, side, lots, product, stop: s, target: t, trail_points: Number(trail) || null, label: inst.label },
      { onSuccess: () => onOpenChange(false) },
    );
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      size="sm"
      title={
        <span className="flex items-center gap-2">
          {side === "BUY" ? "Buy" : "Sell"} {inst.label}
          <Badge tone={live ? "live" : "accent"}>{live ? "LIVE" : "Paper"}</Badge>
        </span>
      }
      description={`Marketable limit order at about ${ref ? fmtPrice(ref) : "n/a"}, through the account risk checks.`}
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant={live ? "live" : "primary"}
            loading={place.isPending}
            disabled={!ref || wrongStop || wrongTarget || (stopNeeded && !s)}
            onClick={submit}
            className={cn(!live && side === "SELL" && "bg-[#f23645] hover:bg-[#f23645]")}
          >
            {side === "BUY" ? "Buy" : "Sell"} {qty} {live ? "with real money" : ""}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Segmented value={side} onChange={setSide} options={[{ value: "BUY", label: "Buy" }, { value: "SELL", label: "Sell" }]} />
        <div className="grid grid-cols-2 gap-3">
          <Field label={inst.kind === "EQUITY" ? "Shares" : `Lots (${inst.lot_size} each)`}>
            <NumberInput int value={lots} onValue={(n) => n !== undefined && n >= 1 && setLots(Math.min(n, 9999))} />
          </Field>
          <Field label="Product">
            <Select value={product} onChange={(e) => setProduct(e.target.value)}>
              <option value="INTRADAY">Intraday</option>
              <option value={inst.kind === "EQUITY" ? "DELIVERY" : "CARRYFORWARD"}>{inst.kind === "EQUITY" ? "Delivery" : "Carry forward"}</option>
            </Select>
          </Field>
          <Field label={stopNeeded ? "Stop" : "Stop (optional)"} error={wrongStop ? `Must be ${side === "BUY" ? "below" : "above"} the price` : undefined}>
            <Input type="number" step={inst.tick_size} inputMode="decimal" value={stop} onChange={(e) => setStop(e.target.value)} placeholder={stopNeeded ? "required" : "none"} />
          </Field>
          <Field label="Target (optional)" error={wrongTarget ? `Must be ${side === "BUY" ? "above" : "below"} the price` : undefined}>
            <Input type="number" step={inst.tick_size} inputMode="decimal" value={target} onChange={(e) => setTarget(e.target.value)} placeholder="none" />
          </Field>
          <Field label="Trailing stop, points (optional)" className="col-span-2">
            <Input type="number" min={0} step={inst.tick_size} inputMode="decimal" value={trail} onChange={(e) => setTrail(e.target.value)} placeholder="no trailing" />
          </Field>
        </div>
        <div className="grid grid-cols-3 gap-2 rounded-lg border border-line bg-panel-2 p-2.5 text-[12px]">
          <div>
            <div className="text-muted">Quantity</div>
            <div className="font-semibold num">{qty}</div>
          </div>
          <div>
            <div className="text-muted">Risk at stop</div>
            <div className="font-semibold num text-down">{risk != null ? pnl(-risk) : "premium"}</div>
          </div>
          <div>
            <div className="text-muted">Reward at target</div>
            <div className="font-semibold num text-up">
              {reward != null ? pnl(reward) : "n/a"}
              {risk && reward ? <span className="ml-1 font-normal text-muted">{(reward / risk).toFixed(1)}R</span> : null}
            </div>
          </div>
        </div>
        <div className="text-[11.5px] text-faint">The stop rests at the broker as a stop-limit order. Drag the stop and target lines on the chart to move them later.</div>
        {live && (
          <Callout tone="live" title="Real money">
            This order goes to Angel One. It needs Live trading allowed in Risk and limits, and your password if you have not confirmed it in the last few minutes.
          </Callout>
        )}
      </div>
    </Dialog>
  );
}

// ── lines over the price pane ───────────────────────────────────────────────
// a press that moves less than this is a tap, not a drag: it must not move a
// stop (on LIVE that re-sends the broker's stop), and a finger jitters more
const DRAG_MIN_PX = { mouse: 3, touch: 8 } as const;

function useDrag(onDrop: (y: number) => void, disabled?: boolean) {
  const [dy, setDy] = useState<number | null>(null);
  const start = useRef<{ y0: number; base: number; min: number } | null>(null);
  const reset = () => {
    start.current = null;
    setDy(null);
  };
  const handlers = disabled
    ? {}
    : {
        onPointerDown: (e: RPointerEvent<HTMLElement>, base: number) => {
          e.stopPropagation();
          e.preventDefault();
          (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
          start.current = { y0: e.clientY, base, min: e.pointerType === "mouse" ? DRAG_MIN_PX.mouse : DRAG_MIN_PX.touch };
          setDy(base);
        },
        onPointerMove: (e: RPointerEvent<HTMLElement>) => {
          if (start.current) setDy(start.current.base + e.clientY - start.current.y0);
        },
        onPointerUp: () => {
          const s = start.current;
          if (s && dy != null && Math.abs(dy - s.base) >= s.min) onDrop(dy);
          reset();
        },
        // the browser took the gesture (a scroll, a system gesture): nothing moves
        onPointerCancel: reset,
      };
  return { dy, handlers };
}

function LevelLine({ view, price: level, color, dashed, left, right, onMove, title }: {
  view: ChartView;
  price: number;
  color: string;
  dashed?: boolean;
  left?: ReactNode;
  right?: (dragPrice: number) => ReactNode;
  onMove?: (p: number) => void;
  title?: string;
}) {
  const y0 = view.y(level);
  const { dy, handlers } = useDrag((y) => {
    const p = view.p(y);
    if (p != null && onMove) onMove(p);
  }, !onMove);
  const y = dy ?? y0;
  if (y == null || y < -20 || y > view.height + 20) return null;
  const dragPrice = dy != null ? view.p(dy) ?? level : level;
  const h = handlers as Partial<{ onPointerDown: (e: RPointerEvent<HTMLElement>, b: number) => void; onPointerMove: (e: RPointerEvent<HTMLElement>) => void; onPointerUp: () => void; onPointerCancel: () => void }>;
  return (
    <>
      <div className="absolute left-0" style={{ top: y, width: view.width, borderTop: `1px ${dashed ? "dashed" : "solid"} ${color}` }} />
      {left && (
        <div className="pointer-events-auto absolute left-2 flex -translate-y-1/2 items-center" style={{ top: y }}>
          {left}
        </div>
      )}
      {right && (
        <div
          className={cn("pointer-events-auto absolute flex -translate-y-1/2 items-center gap-1 rounded-[4px] border bg-panel px-1.5 py-0.5 text-[11.5px] shadow-sm select-none", onMove && "cursor-ns-resize touch-none")}
          style={{ top: y, right: 12, borderColor: color, color }}
          title={title}
          onPointerDown={(e) => h.onPointerDown?.(e, y0 ?? 0)}
          onPointerMove={h.onPointerMove}
          onPointerUp={h.onPointerUp}
          onPointerCancel={h.onPointerCancel}
        >
          {right(dragPrice)}
        </div>
      )}
    </>
  );
}

export function PositionLines({ view, positions, onClose, onLevels, signalChart }: {
  view: ChartView;
  positions: Position[];
  onClose: (p: Position) => void;
  onLevels: (p: Position, levels: { stop?: number; target?: number }) => void;
  /** The chart shows the underlying: draw the levels set on it. */
  signalChart?: boolean;
}) {
  const c = chartColors();
  return (
    <>
      {positions.map((p) => {
        const long = p.side === "LONG";
        const manual = p.run_id === "manual" && !signalChart;
        const sign = long ? 1 : -1;
        const stop = signalChart ? p.signal_stop : p.stop;
        const target = signalChart ? p.signal_target : p.target;
        const atLevel = (lv: number) => (lv - p.avg_price) * p.qty * sign;
        return (
          <div key={p.id}>
            {!signalChart && (
              <LevelLine
                view={view}
                price={p.avg_price}
                color={c.accent}
                dashed
                left={
                  <div className="flex overflow-hidden rounded-[4px] border border-accent text-[11.5px] shadow-sm">
                    <span className={cn("px-1.5 py-0.5 font-semibold text-white", long ? "bg-[#089981]" : "bg-[#f23645]")}>
                      {long ? "LONG" : "SHORT"} {p.qty}
                    </span>
                    <span className={cn("bg-panel px-1.5 py-0.5 num", p.unrealized >= 0 ? "text-up" : "text-down")}>{pnl(p.unrealized)}</span>
                    <span className="max-w-28 truncate bg-panel px-1.5 py-0.5 text-muted">{p.strategy_name}</span>
                    {p.status === "OPEN" && (
                      <button className="bg-panel px-1 text-muted hover:bg-down hover:text-white" onClick={() => onClose(p)} aria-label="Close position" title="Close this position">
                        <X className="size-3.5" />
                      </button>
                    )}
                  </div>
                }
              />
            )}
            {stop != null && (
              <LevelLine
                view={view}
                price={stop}
                color={c.down}
                dashed
                onMove={manual ? (v) => onLevels(p, { stop: roundTick(v, p.instrument.tick_size) }) : undefined}
                title={manual ? "Drag to move the stop" : "Managed by the strategy's exit plan"}
                right={(v) => (
                  <>
                    SL {fmtPrice(v)} {!signalChart && <span className="num">{pnl(atLevel(v))}</span>}
                  </>
                )}
              />
            )}
            {target != null && (
              <LevelLine
                view={view}
                price={target}
                color={c.up}
                dashed
                onMove={manual ? (v) => onLevels(p, { target: roundTick(v, p.instrument.tick_size) }) : undefined}
                title={manual ? "Drag to move the target" : "Managed by the strategy's exit plan"}
                right={(v) => (
                  <>
                    TP {fmtPrice(v)} {!signalChart && <span className="num">{pnl(atLevel(v))}</span>}
                  </>
                )}
              />
            )}
          </div>
        );
      })}
    </>
  );
}

// ── price alerts ────────────────────────────────────────────────────────────
export interface PriceAlert {
  id: string;
  key: string;
  label: string;
  condition: "crosses" | "crosses_above" | "crosses_below";
  price: number;
  message: string;
  repeat: boolean;
  status: "active" | "triggered";
  created_at: string | null;
  triggered_at: string | null;
  trigger_price: number | null;
}

const COND_LABEL: Record<PriceAlert["condition"], string> = { crosses: "Crossing", crosses_above: "Crossing up", crosses_below: "Crossing down" };

export function useAlerts() {
  return useQuery({ queryKey: ["alerts"], queryFn: () => api.get<{ alerts: PriceAlert[] }>("/api/alerts").then((r) => r.alerts), staleTime: 30_000 });
}

/** Toast (and a browser notification when allowed) whenever an alert fires, anywhere in the app. */
export function usePriceAlertToasts() {
  const qc = useQueryClient();
  useEffect(
    () =>
      subscribe("alert", (a: LiveAlert) => {
        if (a.code !== "price_alert") return;
        qc.invalidateQueries({ queryKey: ["alerts"] });
        toast.warning(a.message, { icon: <BellRing className="size-4" />, duration: 12_000 });
        try {
          if ("Notification" in window && Notification.permission === "granted") new Notification("AlgoTrader alert", { body: a.message });
        } catch {
          /* notifications unavailable */
        }
      }),
    [qc],
  );
}

export function AlertLines({ view, alerts }: { view: ChartView; alerts: PriceAlert[] }) {
  const qc = useQueryClient();
  const c = chartColors();
  const move = useMutation({
    mutationFn: ({ id, price }: { id: string; price: number }) => api.patch(`/api/alerts/${id}`, { price }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["alerts"] }),
    onError: (e) => toast.error(errorMessage(e)),
  });
  const del = useMutation({
    mutationFn: (id: string) => api.del(`/api/alerts/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["alerts"] }),
  });
  return (
    <>
      {alerts.map((a) => (
        <LevelLine
          key={a.id}
          view={view}
          price={a.price}
          color={c.warn}
          dashed
          title="Drag to move the alert"
          onMove={(p) => move.mutate({ id: a.id, price: Number(p.toFixed(2)) })}
          right={(v) => (
            <>
              <Bell className="size-3" />
              {fmtPrice(v)}
              <button
                className="ml-0.5 rounded text-muted hover:text-down"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={() => del.mutate(a.id)}
                aria-label="Delete alert"
              >
                <X className="size-3" />
              </button>
            </>
          )}
        />
      ))}
    </>
  );
}

export function AlertDialog({ open, onOpenChange, inst, price: price0 }: { open: boolean; onOpenChange: (o: boolean) => void; inst: Instrument | null; price: number | null }) {
  const qc = useQueryClient();
  const [condition, setCondition] = useState<PriceAlert["condition"]>("crosses");
  const [price, setPrice] = useState("");
  const [message, setMessage] = useState("");
  const [repeat, setRepeat] = useState(false);
  useEffect(() => {
    if (open) {
      setPrice(price0 ? roundTick(price0, inst?.tick_size).toFixed(2) : "");
      setMessage("");
    }
  }, [open, price0, inst?.tick_size]);
  const create = useMutation({
    mutationFn: () => api.post("/api/alerts", { key: inst!.key, price: Number(price), condition, message, repeat }),
    onSuccess: () => {
      toast.success(`Alert set: ${inst!.label} ${COND_LABEL[condition].toLowerCase()} ${fmtPrice(Number(price))}`);
      qc.invalidateQueries({ queryKey: ["alerts"] });
      onOpenChange(false);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const canNotify = typeof window !== "undefined" && "Notification" in window && Notification.permission === "default";
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      size="sm"
      title={`Create alert on ${inst?.label ?? ""}`}
      description="The engine checks it on every price update, also while this page is closed. Telegram gets it when set up in Settings."
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="primary" loading={create.isPending} disabled={!inst || !(Number(price) > 0)} onClick={() => create.mutate()}>
            Create
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-3">
        <Field label="Condition">
          <Select value={condition} onChange={(e) => setCondition(e.target.value as PriceAlert["condition"])}>
            {Object.entries(COND_LABEL).map(([v, l]) => (
              <option key={v} value={v}>
                {l}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Price">
          <Input type="number" step={inst?.tick_size ?? 0.05} value={price} onChange={(e) => setPrice(e.target.value)} autoFocus />
        </Field>
        <Field label="Message (optional)" className="col-span-2">
          <Input value={message} maxLength={200} onChange={(e) => setMessage(e.target.value)} placeholder="Breakout above the morning high" />
        </Field>
        <Field label="Trigger" className="col-span-2">
          <Segmented value={repeat ? "every" : "once"} onChange={(v) => setRepeat(v === "every")} options={[{ value: "once", label: "Only once" }, { value: "every", label: "Every time (at most once a minute)" }]} />
        </Field>
      </div>
      {canNotify && (
        <button className="mt-3 text-[12px] text-accent hover:underline" onClick={() => void Notification.requestPermission()}>
          Also show alerts as notifications in this browser
        </button>
      )}
    </Dialog>
  );
}

export function AlertsSheet({ open, onOpenChange, onOpenChart }: { open: boolean; onOpenChange: (o: boolean) => void; onOpenChart: (key: string) => void }) {
  const qc = useQueryClient();
  const alerts = useAlerts();
  const act = useMutation({
    mutationFn: ({ id, op }: { id: string; op: "rearm" | "delete" }) => (op === "delete" ? api.del(`/api/alerts/${id}`) : api.patch(`/api/alerts/${id}`, { active: true })),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["alerts"] }),
    onError: (e) => toast.error(errorMessage(e)),
  });
  const list = alerts.data ?? [];
  const groups = useMemo(() => [
    { title: "Active", items: list.filter((a) => a.status === "active") },
    { title: "Triggered", items: list.filter((a) => a.status !== "active") },
  ], [list]);
  return (
    <Sheet open={open} onOpenChange={onOpenChange} title="Alerts" description="Price alerts across every instrument." width="max-w-md">
      {list.length === 0 ? (
        <Empty icon={<Bell className="size-5" />} title="No alerts yet">
          Use the alarm button on the chart toolbar, or Alt + A, to alert on a price.
        </Empty>
      ) : (
        groups.map(
          (g) =>
            g.items.length > 0 && (
              <div key={g.title} className="mb-4">
                <div className="mb-1.5 text-[11px] font-medium tracking-wide text-muted uppercase">{g.title}</div>
                <div className="divide-y divide-line rounded-lg border border-line">
                  {g.items.map((a) => (
                    <div key={a.id} className="flex items-center gap-2 px-3 py-2 text-[12.5px]">
                      <button className="min-w-0 flex-1 text-left" onClick={() => onOpenChart(a.key)}>
                        <div className="truncate font-medium">{a.label}</div>
                        <div className="text-[11.5px] text-muted">
                          {COND_LABEL[a.condition]} {fmtPrice(a.price)}
                          {a.repeat ? ", every time" : ""}
                          {a.message ? `: ${a.message}` : ""}
                        </div>
                        {a.triggered_at && (
                          <div className="text-[11px] text-faint">
                            Fired {ago(a.triggered_at)} at {fmtPrice(a.trigger_price)}
                          </div>
                        )}
                      </button>
                      {a.status !== "active" && (
                        <Button size="icon-sm" variant="ghost" aria-label="Re-arm" onClick={() => act.mutate({ id: a.id, op: "rearm" })}>
                          <RotateCcw className="size-3.5" />
                        </Button>
                      )}
                      <Button size="icon-sm" variant="ghost" aria-label="Delete" onClick={() => act.mutate({ id: a.id, op: "delete" })}>
                        <Trash2 className="size-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            ),
        )
      )}
    </Sheet>
  );
}
